import { useEffect, useState } from "react";
import { Avatar, Modal } from "./ui";
import { Icon } from "./Icons";
import { actions, useRoleMatrix, STUDIO } from "../lib/store";
import { useSession, useIsAdmin } from "../lib/auth";
import { openWhatsApp, teamInviteText } from "../lib/whatsapp";
import { apiMembers, apiSetRole, apiCreateInvite, apiInvites, apiRevokeInvite, type Member, type Invite } from "../lib/authApi";
import { ROLES, CAPABILITIES, type RoleKey, type Capability } from "../lib/permissions";

/* Accounts + the role×capability matrix. Rendered both inside a project (Team
   page) and at portfolio level from Projects, so the admin can grant someone
   "New projects" without opening a project they may not have access to. */

const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.key, r.label])) as Record<string, string>;

export function TeamPanel({ compact }: { compact?: boolean }) {
  const matrix = useRoleMatrix();
  const session = useSession();
  const isAdmin = useIsAdmin();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const [invites, setInvites] = useState<Invite[]>([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [f, setF] = useState({ email: "", name: "", role: "designer" as RoleKey });
  const [made, setMade] = useState<Invite | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() { setMembers(await apiMembers()); setInvites(await apiInvites()); setLoading(false); }
  useEffect(() => { if (isAdmin) refresh(); else setLoading(false); }, [isAdmin]);

  async function changeRole(email: string, role: RoleKey) {
    setMembers((m) => m.map((x) => x.email === email ? { ...x, role } : x)); // optimistic
    try { await apiSetRole(email, role); } catch { refresh(); }
  }
  const inviteLink = (t: string) => `${location.origin}/login?invite=${t}`;
  function copyInvite(token: string) {
    navigator.clipboard?.writeText(inviteLink(token));
    setCopied(true); setTimeout(() => setCopied(false), 2200);
  }
  async function createInvite() {
    setBusy(true); setErr(null);
    try {
      const inv = await apiCreateInvite(f.email.trim(), f.name.trim(), f.role);
      setMade(inv); refresh();
    } catch (e: any) { setErr(e.message || "Couldn't create that invitation."); }
    finally { setBusy(false); }
  }
  async function revoke(token: string) { await apiRevokeInvite(token); refresh(); }
  function closeInvite() { setInviteOpen(false); setMade(null); setErr(null); setF({ email: "", name: "", role: "designer" }); }

  const others = members.filter((m) => m.email !== session?.email);

  return (
    <>
      {/* you */}
      {!compact && (
        <div className="card pad reveal" style={{ marginBottom: 18, borderColor: "color-mix(in srgb, var(--accent) 35%, var(--line))", background: "color-mix(in srgb, var(--accent) 5%, var(--card))" }}>
          <div className="row between">
            <div className="row" style={{ gap: 12 }}>
              <Avatar name={session?.name ?? STUDIO.principal} hue={20} size={44} />
              <div>
                <div className="row" style={{ gap: 8 }}><span style={{ fontWeight: 600 }}>{session?.name ?? STUDIO.principal}</span><span className="badge accent"><span className="dot" />You</span></div>
                <div className="dim" style={{ fontSize: 12.5 }}>{session?.email ?? STUDIO.email}</div>
              </div>
            </div>
            <div style={{ textAlign: "right" }}><div className="badge" style={{ marginBottom: 4 }}>{ROLE_LABEL[session?.teamRole ?? "admin"]}</div><div className="dim" style={{ fontSize: 11.5 }}>{isAdmin ? "Full access · always on" : ""}</div></div>
          </div>
        </div>
      )}

      {/* accounts */}
      {isAdmin && (
        <>
          <div className="row between" style={{ marginBottom: 10 }}>
            <span className="kicker">Accounts</span>
            <button className="btn accent sm" onClick={() => setInviteOpen(true)}><Icon.plus width={14} />Invite team member</button>
          </div>
          <div className="card" style={{ overflow: "hidden", marginBottom: 22 }}>
            {loading && <div className="dim" style={{ padding: 22, textAlign: "center" }}>Loading accounts…</div>}
            {!loading && others.length === 0 && <div className="dim" style={{ padding: 22, textAlign: "center", fontSize: 13.5 }}>No other accounts yet. Share the invite link so your team can sign up.</div>}
            {others.map((m, i) => (
              <div key={m.email} className="row between" style={{ padding: "14px 18px", borderTop: i ? "1px solid var(--line)" : "none", gap: 12 }}>
                <div className="row" style={{ gap: 12, minWidth: 0 }}>
                  <Avatar name={m.name || m.email} hue={(m.email.charCodeAt(0) * 7) % 360} size={36} />
                  <div style={{ minWidth: 0 }}><div style={{ fontWeight: 600, fontSize: 14 }}>{m.name || m.email.split("@")[0]}</div><div className="dim" style={{ fontSize: 12 }}>{m.email}</div></div>
                </div>
                <select className="select" style={{ width: "auto", padding: "7px 10px", fontSize: 13 }} value={m.role} onChange={(e) => changeRole(m.email, e.target.value as RoleKey)}>
                  {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
              </div>
            ))}
          </div>

          {/* invited, not yet joined */}
          {invites.length > 0 && (
            <>
              <div className="row between" style={{ marginBottom: 10 }}>
                <span className="kicker">Invited — not joined yet</span>
                {copied && <span className="badge ok"><Icon.check width={12} />Link copied</span>}
              </div>
              <div className="card" style={{ overflow: "hidden", marginBottom: 22 }}>
                {invites.map((iv, i) => (
                  <div key={iv.token} className="row between wrap" style={{ padding: "12px 18px", borderTop: i ? "1px solid var(--line)" : "none", gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 500 }}>{iv.name || iv.email || "Anyone with the link"}</div>
                      <div className="dim" style={{ fontSize: 12 }}>{iv.email || "no address — the link is the invitation"} · {ROLE_LABEL[iv.role] ?? iv.role}</div>
                    </div>
                    <div className="row" style={{ gap: 6 }}>
                      <button className="chip" onClick={() => copyInvite(iv.token)}><Icon.link width={12} />Copy link</button>
                      <button className="chip" style={{ color: "#25D366", borderColor: "#25D366" }} onClick={() => openWhatsApp(teamInviteText({ name: iv.name || "there", studio: STUDIO.name, role: ROLE_LABEL[iv.role] ?? iv.role, url: inviteLink(iv.token) }))}><Icon.whatsapp width={12} />WhatsApp</button>
                      <button className="iconbtn" style={{ width: 28, height: 28 }} title="Withdraw" onClick={() => revoke(iv.token)}><Icon.close width={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* permissions matrix */}
      <div className="row between" style={{ marginBottom: 10 }}>
        <span className="kicker">Permissions by role</span>
        {isAdmin && <button className="btn ghost sm" onClick={() => actions.resetRoleMatrix()}>Reset to defaults</button>}
      </div>
      <p className="muted" style={{ fontSize: 13.5, marginBottom: 12, maxWidth: 640 }}>
        {isAdmin
          ? <>Toggle what each role can change — this table is the enforcement. <b>New projects</b> can be granted on its own, so someone can start an engagement without reaching any other section.</>
          : "What each role can do. Only the admin can change this."}
      </p>
      <div className="card" style={{ padding: 0, overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 720 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "14px 18px", position: "sticky", left: 0, background: "var(--card)", zIndex: 1 }}><span className="kicker">Capability</span></th>
              {ROLES.map((r) => <th key={r.key} style={{ padding: "14px 10px", textAlign: "center", minWidth: 88 }}><div style={{ fontSize: 12.5, fontWeight: 600 }}>{r.label}</div>{r.locked && <div className="dim" style={{ fontSize: 9.5 }}>locked</div>}</th>)}
            </tr>
          </thead>
          <tbody>
            {CAPABILITIES.map((cap) => (
              <tr key={cap.key} style={{ borderTop: "1px solid var(--line)" }}>
                <td style={{ padding: "12px 18px", fontSize: 13.5, fontWeight: 500, position: "sticky", left: 0, background: "var(--card)" }}>{cap.label}</td>
                {ROLES.map((r) => (
                  <td key={r.key} style={{ padding: "10px", textAlign: "center" }}>
                    <Cell on={!!matrix[r.key]?.[cap.key]} locked={!!r.locked || !isAdmin} onToggle={() => actions.setRolePermission(r.key as RoleKey, cap.key as Capability, !matrix[r.key]?.[cap.key])} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={inviteOpen} onClose={closeInvite} title="Invite a team member">
        {!made ? (
          <div className="col" style={{ gap: 14 }}>
            <p className="muted" style={{ fontSize: 14, lineHeight: 1.6 }}>
              They'll get a link, choose their own password, and land in the cockpit with exactly the role you pick here. Joining is invitation-only — the link is single-use.
            </p>
            <div className="field"><label>Their name</label><input className="input" autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Prem" /></div>
            <div className="field">
              <label>Their email <span className="dim">— optional, but it locks the invite to them</span></label>
              <input className="input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="name@hddesignstudiogy.com" />
            </div>
            <div className="field">
              <label>Role</label>
              <select className="select" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value as RoleKey })}>
                {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label} — {r.blurb}</option>)}
              </select>
            </div>
            {err && <span style={{ color: "var(--danger)", fontSize: 12.5 }}>{err}</span>}
            <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
              <button className="btn ghost" onClick={closeInvite}>Cancel</button>
              <button className="btn accent" onClick={createInvite} disabled={busy || !f.name.trim()}>{busy ? "…" : "Create invitation"}<Icon.arrow width={15} /></button>
            </div>
          </div>
        ) : (
          <div className="col" style={{ gap: 14 }}>
            <div className="row" style={{ gap: 8 }}>
              <span className="badge ok"><Icon.check width={12} />Invitation ready</span>
              <span className="dim" style={{ fontSize: 12.5 }}>{made.name || made.email} · {ROLE_LABEL[made.role] ?? made.role}</span>
            </div>
            <div className="panel" style={{ padding: 12 }}>
              <div className="kicker" style={{ fontSize: 9 }}>Their link</div>
              <div className="mono" style={{ fontSize: 12, marginTop: 4, wordBreak: "break-all" }}>{inviteLink(made.token)}</div>
            </div>
            <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
              Single-use{made.email ? ` and tied to ${made.email}` : ""}. They pick their own password — you never handle it.
            </p>
            <div className="row wrap" style={{ gap: 10 }}>
              <button className="btn ghost" onClick={() => copyInvite(made.token)}>{copied ? <><Icon.check width={15} />Copied</> : <><Icon.link width={15} />Copy link</>}</button>
              <button className="btn" style={{ background: "#25D366", borderColor: "#25D366", color: "#fff" }}
                onClick={() => openWhatsApp(teamInviteText({ name: made.name || "there", studio: STUDIO.name, role: ROLE_LABEL[made.role] ?? made.role, url: inviteLink(made.token) }))}>
                <Icon.whatsapp width={16} />Send on WhatsApp
              </button>
              <button className="btn accent" style={{ marginLeft: "auto" }} onClick={closeInvite}>Done</button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

function Cell({ on, locked, onToggle }: { on: boolean; locked: boolean; onToggle: () => void }) {
  return (
    <button onClick={locked ? undefined : onToggle} disabled={locked} title={locked ? "Locked" : on ? "Allowed — click to revoke" : "Denied — click to allow"}
      style={{ width: 26, height: 26, borderRadius: 8, cursor: locked ? "default" : "pointer", display: "grid", placeItems: "center", margin: "0 auto",
        border: "1.5px solid " + (on ? "var(--accent)" : "var(--line-2)"), background: on ? "var(--accent)" : "transparent", color: "#fff", opacity: locked && !on ? 0.4 : 1 }}>
      {on && <Icon.check width={14} />}
    </button>
  );
}
